import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';

import {
  CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY,
  CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY,
} from './useConvenienceDispatchDeliveries.query';
import { USE_DELIVERIES_QUERY_KEY } from './useDeliveries.query';
import {
  PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY,
  USE_QUERY_PLANNINGS_DELIVERIES_QUERY_KEY,
} from './useQueryPlanningsDeliveries';

const REQUEST_PATH = '/api/v5/convenience_dispatches/shift_trucks';

type RequestBody = {
  truck_id: number;
  start_at: string;
  end_at: string;
  driver_id?: number;
  labor_hours?: number;
};

export const useConvenienceDispatchShiftTruckMutation = () => {
  const queryClient = useQueryClient();

  // shift_truck の create / destroy / update は、固定ルート画面以外の
  // 勤務計画 (Shifts) / 配車計画 (V3Plans / V3Timeline / TrunkTransportation) の
  // deliveries 表示にも影響する。これらの query は staleTime: Infinity なので、
  // 明示的に invalidate しないと別画面へ遷移しても古いキャッシュが残る。
  // また CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY は「本日なし判定」用に
  // 固定ルート選択時に保持され、AddVehicleDialog が mutation 単独で呼ぶ経路
  // (Presenter の refetch を通らない) でも cache が古くなるため必須。
  const invalidateRelatedDeliveries = () => {
    void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: [USE_DELIVERIES_QUERY_KEY] }); // Shifts ページ
    void queryClient.invalidateQueries({ queryKey: [USE_QUERY_PLANNINGS_DELIVERIES_QUERY_KEY] }); // v3 deliveries
    void queryClient.invalidateQueries({ queryKey: [PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY] }); // v5 deliveries
  };

  return {
    create: useMutation(
      (body: RequestBody) => axios.post<PlanningsDeliveryWithLegRunEntity>(REQUEST_PATH, body),
      {
        onSuccess: (_response: AxiosResponse<PlanningsDeliveryWithLegRunEntity>) => {
          invalidateRelatedDeliveries();
        },
      }
    ),
    // OFF にする操作: leg_run.operations を resetting (= 未割当に戻す) + shift_truck/shift を destroy
    destroy: useMutation(
      (shiftTruckId: number) => axios.delete<void>(`${REQUEST_PATH}/${shiftTruckId}`),
      {
        onSuccess: () => {
          invalidateRelatedDeliveries();
        },
      }
    ),
    // 車両 / ドライバーの差し替え (アルゴリズム再計算なし)。
    // truck_id / driver_id のいずれかまたは両方を渡す。backend で uniqueness 衝突を回避する。
    update: useMutation(
      (args: { shiftTruckId: number; truckId?: number; driverId?: number }) => axios.patch(
        `${REQUEST_PATH}/${args.shiftTruckId}`,
        { truck_id: args.truckId, driver_id: args.driverId },
      ),
      {
        onSuccess: () => {
          invalidateRelatedDeliveries();
        },
      },
    ),
  };
};
